import React, { useEffect, useState } from 'react';
import Config from '../config';
import { Button, LinearProgress } from '@mui/material';
import Helpers from '../helpers';
import axios from 'axios';
import FileSaverBase from 'filesaver.js-npm';

// filesaver.js-npm ships no type definitions; treat as untyped, matching the
// pattern already used in edi_viewer.tsx.
const FileSaver: any = FileSaverBase;

interface DownloadButtonProps {
    href?: string;
    // Progress-aware callers receive setProgress; plain submit handlers
    // (redux-form handleSubmit, etc.) ignore it. Accept any function so both
    // call shapes typecheck — the ccn onboarding dialogs pass handleSubmit.
    onClick?: (...args: any[]) => any;
    children?: React.ReactNode;
    handleComplete?: () => void;
    loading?: boolean;
    onError?: (error: string) => void;
    fileName?: string;
    progressclasses?: any;
    style?: React.CSSProperties;
    [key: string]: any;
}

const DownloadButton = (props: DownloadButtonProps) => {
    const [completed, setCompleted] = useState<number>(0);
    const [determinate, setDeterminate] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [href, setHref] = useState<string | undefined>(undefined);
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [fileName, setFileName] = useState<string | undefined>(undefined);

    // Mirrors componentDidMount + componentDidUpdate: recompute the resolved
    // link after every render, guarded so it only updates state when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (props.href) {
            let replacedLink = new Helpers().replaceBlobStorageLinks(props.href);
            if (href !== replacedLink) {
                var nextUrl = replacedLink.indexOf("/") === 0 ? Config.api + "/api/v1/attachments/" + replacedLink.split('/storage/')[1] : props.href;
                var split = nextUrl.split("/");
                setHref(replacedLink);
                setUrl(nextUrl);
                setFileName(props.fileName || split[split.length - 1]);
            }
        }
    });

    const setProgress = (progress?: number) => {
        if (progress) {
            setDeterminate(true);
            setCompleted((prevCompleted) => prevCompleted < progress ? Math.floor(progress) : prevCompleted);
        } else {
            setDeterminate(false);
            setCompleted(0);
        }
    };

    const onClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (!loading) {
            var clickUrl = url;
            var clickFileName = fileName;
            setLoading(true);
            setDeterminate(false);
            setCompleted(0);
            if (props.onClick) {
                var clickRes: any = await props.onClick(setProgress);
                if (typeof clickRes === "string") {
                    clickUrl = clickRes;
                } else if (typeof clickRes === "object") {
                    clickUrl = clickRes.href;
                    clickFileName = clickRes.fileName;
                }
            }
            if (!clickUrl) {
                setLoading(false);
                if (props.handleComplete) {
                    props.handleComplete();
                }
                return;
            }
            try {
                let res = await axios({
                    url: clickUrl,
                    method: 'GET',
                    responseType: 'blob',
                    onDownloadProgress: function (progressEvent) {
                        if (progressEvent.total) {
                            var progress = parseInt((100 * (progressEvent.loaded / progressEvent.total)).toFixed(0));
                            setDeterminate(true);
                            setCompleted((prevCompleted) => prevCompleted < progress ? progress : prevCompleted);
                        }
                    }
                });
                let blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
                FileSaver.saveAs(blob, clickFileName);
            } catch (ex: any) {
                if (props.onError) {
                    var error = 'An unexpected error occurred.';
                    if (ex.response && ex.response.data) {
                        var data = ex.response.data;
                        try {
                            data = JSON.parse(await new Helpers().readBlob(data));
                        } catch {
                            // do nothing
                        }
                        error = new Helpers().getApiErrors(data).join("\n")
                    }
                    setLoading(false);
                    props.onError(error);
                }
                return;
            }
            setLoading(false);
            if (props.handleComplete) {
                props.handleComplete();
            }
        }
    };

    let { href: hrefProp, onClick: onClickProp, children, handleComplete, loading: loadingProp, onError, fileName: fileNameProp, progressclasses, ...other } = props;
    return <Button {...other} href={href} onClick={onClick} style={{ pointerEvents: loading || loadingProp ? 'none' : undefined, ...props.style }}><div style={{ visibility: loading || loadingProp ? 'hidden' : undefined }}>{children}</div>{loading || loadingProp ?
        <div style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0 }}>
            <LinearProgress
                sx={(theme) => ({
                    backgroundColor: `${theme.palette.primary.main}66`,
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: theme.palette.primary.main,
                    }
                })}
                style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0 }}
                variant={determinate ? 'determinate' : 'indeterminate'}
                value={completed} />
            <div style={{ top: 0, left: 0, position: 'absolute', width: '100%', height: '100%', zIndex: '50', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{determinate ? (<div>{completed}%</div>) : ''}</div>
        </div>
        : ''}</Button>
};

export default DownloadButton;
